import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { motion } from 'framer-motion';
import { Play, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLectures, getTranslation } from '@/hooks/useSupabaseData';
import { Skeleton } from '@/components/ui/skeleton';

const FeaturedLecturesSection = () => {
  const { t, lang, localePath } = useLanguage();
  const Arrow = lang === 'en' ? ArrowRight : ArrowLeft;
  const { data: allLectures, isLoading } = useLectures(false);
  const displayLectures = allLectures;
  const scrollRef = useRef<HTMLDivElement>(null);
  const isRtl = lang === 'he' || lang === 'ar';

  const scroll = (direction: 'prev' | 'next') => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const cardWidth = container.querySelector('.snap-center')?.getBoundingClientRect().width || 300;
    const scrollAmount = cardWidth + 24;
    const sign = direction === 'next' ? 1 : -1;
    const rtlFlip = isRtl ? -1 : 1;
    container.scrollBy({ left: sign * rtlFlip * scrollAmount, behavior: 'smooth' });
  };

  const PrevIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gradient-gold mb-4">{t('section.lectures_title')}</h2>
        </motion.div>

        <div className="relative group/section">
          <Button
            variant="ghost"
            size="icon"
            className="absolute start-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 backdrop-blur border border-border/50 shadow-lg md:opacity-0 md:group-hover/section:opacity-100 transition-opacity rounded-full"
            onClick={() => scroll('prev')}
            aria-label="Previous"
          >
            <PrevIcon className="w-5 h-5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="absolute end-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 backdrop-blur border border-border/50 shadow-lg md:opacity-0 md:group-hover/section:opacity-100 transition-opacity rounded-full"
            onClick={() => scroll('next')}
            aria-label="Next"
          >
            <NextIcon className="w-5 h-5" />
          </Button>

          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide px-8 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 w-72 shrink-0 rounded-xl" />)
            ) : displayLectures?.length ? (
              displayLectures.map((lecture, i) => {
                const tr = getTranslation(lecture.lecture_translations, lang);
                const hasVideo = !!lecture.video_url;
                const Wrapper = hasVideo ? 'a' : 'div';
                const wrapperProps = hasVideo
                  ? { href: lecture.video_url!, target: '_blank' as const, rel: 'noopener noreferrer' }
                  : {};
                return (
                  <motion.div
                    key={lecture.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="snap-center shrink-0 w-72 md:w-80"
                  >
                    <Wrapper {...wrapperProps} className="block no-underline">
                      <Card className={`bg-card/50 border-border/50 hover:border-primary/30 transition-all h-full group ${hasVideo ? 'cursor-pointer' : ''}`}>
                        <CardContent className="p-0">
                          <div className="aspect-video bg-navy-light flex items-center justify-center group-hover:bg-navy-medium transition-colors overflow-hidden relative rounded-t-lg">
                            {lecture.image_url ? (
                              <img src={lecture.image_url} alt={tr?.title || ''} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                                <Play className="w-6 h-6 text-primary ms-1" />
                              </div>
                            )}
                            {hasVideo && (
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                <div className="w-14 h-14 rounded-full bg-primary/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity scale-75 group-hover:scale-100">
                                  <Play className="w-6 h-6 text-primary-foreground ms-1" />
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="p-5">
                            {lecture.duration && <p className="text-xs text-primary mb-2">{lecture.duration}</p>}
                            <h3 className="text-base font-semibold mb-1 text-foreground line-clamp-2">{tr?.title || ''}</h3>
                            {tr?.description && (
                              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{tr.description}</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Wrapper>
                  </motion.div>
                );
              })
            ) : null}
          </div>
        </div>

        <div className="text-center mt-10">
          <Button asChild variant="outline" className="rounded-full px-8 border-primary/30 hover:bg-primary/10 text-primary gap-2">
            <Link to={localePath('/lectures')}>
              {t('section.lectures_title')}
              <Arrow className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedLecturesSection;
